<template>
  <Vuedraggable
    v-if="!!props.modelValue"
    :list="props.modelValue"
    class="tour"
    draggable=".tour-day-container:not(.not-draggable)"
    item-key="id"
    v-bind="dragOptions"
    @change="change"
  >
    <template #item="{ element, index }">
      <div
        :key="index"
        :class="{
          'not-draggable ghost-item': props.updatingTour,
          'not-draggable blocked-item': element.isBlocked,
        }"
        class="tour-day-container"
      >
        <div key="tour-day">
          <v-button
            v-if="index !== 0"
            :disabled="props.updatingTour"
            class="btn-icon btn-day p-4 mb-12"
            size="sm"
            variant="outline-secondary"
            @click="addDay(index)"
          >
            <v-icon icon="plus" />
          </v-button>

          <div :class="{ 'mt-36': index === 0 }" class="tour-day d-flex flex-row row gx-0">
            <div class="col-4">
              <div class="tour-day-info p-20 pe-16">
                <div class="d-flex align-items-center">
                  <div class="tour-day-info-left me-32">
                    <div class="day-circle d-flex align-items-center justify-content-center mb-8">
                      <div>{{ index + 1 }}</div>
                    </div>
                  </div>

                  <div class="tour-day-info-right w-100">
                    <div class="d-flex align-items-center mb-12">
                      <v-checkbox
                        :id="`template-day-${element.id}-stage`"
                        :is-disabled="isTourDayUpdating"
                        :label="$t('template.stage')"
                        :left="false"
                        :modelValue="element.isStage"
                        :right="true"
                        class="form-check-inline me-32"
                        for-key="isAllDayActivated"
                        @update:modelValue="updateTourDayType($event, element)"
                      />
                    </div>

                    <template v-if="element.isStage">
                      <v-input-address
                        id="template-day-origin-search"
                        ref="template-day-origin-search-ref"
                        :disabled-input="isTourDayUpdating"
                        :get-address="true"
                        :get-radius-bounds="true"
                        :label="$t('general.shared.origin')"
                        :radius-bounds="20000"
                        :value="searchLocation(element.originId)"
                        class="col-12 mb-20"
                        @address="createLocation(element, $event, true, index)"
                        @input="updateTourDayOrigin($event, element, index)"
                      />

                      <v-input-address
                        id="template-day-destination-search"
                        ref="template-day-destination-search-ref"
                        :disabled-input="isTourDayUpdating"
                        :get-address="true"
                        :get-radius-bounds="true"
                        :label="$t('general.shared.destination')"
                        :radius-bounds="20000"
                        :value="searchLocation(element.destinationId)"
                        class="col-12 mb-20"
                        @address="createLocation(element, $event, false, index)"
                        @input="updateTourDayDestination($event, element)"
                      />

                      <div class="text-sm mb-20">{{ $t("general.shared.distanceKm") }}: {{ element.stageLength }}</div>
                    </template>

                    <template v-else>
                      <v-input-address
                        id="template-day-location-search"
                        ref="template-day-location-search-ref"
                        :disabled-input="isTourDayUpdating"
                        :get-address="true"
                        :get-radius-bounds="true"
                        :label="$t('general.shared.location')"
                        :radius-bounds="20000"
                        :value="searchLocation(element.originId)"
                        class="col-12 mb-20"
                        @address="createLocation(element, $event, true, index)"
                        @input="updateTourDayOrigin($event, element)"
                      />
                    </template>

                    <v-button
                      :class="{ active: props.currentTourDay === element.id }"
                      class="btn-icon"
                      size="xs"
                      variant="outline-default"
                      @click="emit('openDayInformationOffCanvas', element.id, index + 1)"
                    >
                      <v-icon icon="pencil" space="me-12" />
                      {{ $t("tourBuilder.editStageInformation") }}
                    </v-button>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-8">
              <div class="d-flex justify-content-between h-100 align-items-center p-20 ps-16">
                <the-template-day-item-draggable-v2
                  :agency="agency"
                  :can-add-item="
                    (element.type !== 'stage' && !!element.originId) ||
                    (element.type === 'stage' && !!element.originId && !!element.destinationId)
                  "
                  :list="element.items"
                  :template-day-id="element.id"
                  :template-id="templateId"
                  :tour-mapped="props.tourMapped"
                  :updating-tour="props.updatingTour"
                  :origin-id="element.originId"
                  :destination-id="element.destinationId"
                  type="tour-day-item"
                  @emit-reload-tour="emit('reloadTour')"
                  @open-item-details-off-canvas="emit('openItemDetailsOffCanvas', $event)"
                  @move-item-to-another-day="emit('moveItemToAnotherDay', $event)"
                />

                <div class="d-flex flex-column">
                  <v-button
                    :disabled="props.updatingTour"
                    class="btn-day p-4 mb-12"
                    size="sm"
                    variant="icon"
                    @click="emit('reloadTour')"
                  >
                    <span class="icon icon-refresh text-lg text-primary" />
                  </v-button>

                  <v-button
                    :disabled="props.updatingTour"
                    class="btn-day p-4 mb-12"
                    size="sm"
                    variant="icon"
                    @click="removeDay(element, index)"
                  >
                    <v-icon icon="trash" size="lg" />
                  </v-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Vuedraggable>
</template>

<script lang="ts" setup>
import { computed, onMounted, getCurrentInstance, PropType, ref } from "vue";
import Vuedraggable from "vuedraggable";
import { VCheckbox } from "@uniqoders/form";
import { TYPE, useToast } from "vue-toastification";
import { DateTime } from "luxon";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import api from "@/api";
import VInputAddress from "@/components/vendor/basic/form/VInputAddress.vue";
import Location from "@/api/objects/Location";
import { findConceptByKey } from "@/helpers/ConceptHelper";
import TheTemplateDayItemDraggableV2 from "@/components/template/V2/TheTemplateDayItemDraggableV2.vue";
import useOffCanvasUtils from "@/helpers/OffCanvasUtils";
import { useI18n } from "vue-i18n";

const emit = defineEmits([
  "dayMoved",
  "emitAddDay",
  "reloadTour",
  "openItemDetailsOffCanvas",
  "moveItemToAnotherDay",
  "openDayInformationOffCanvas",
]);

const props = defineProps({
  modelValue: {
    type: Array as PropType<Record<string, unknown>[]>,
    required: false,
  },
  type: {
    type: String,
    required: false,
    default: "",
  },
  templateId: {
    type: Number,
    required: true,
  },
  agency: {
    type: String,
    required: true,
  },
  updatingTour: {
    type: Boolean,
    required: false,
    default: false,
  },
  initDate: {
    type: String,
    required: true,
    default: "",
  },
  endDate: {
    type: String,
    required: true,
    default: "",
  },
  currentTourDay: {
    type: [String, Number],
    required: false,
    default: "",
  },
  tourMapped: {
    type: Object,
    required: true,
  },
});

const isStage = ref(false);
const isTourDayUpdating = ref(false);
const locations = ref<Location[]>([]);
const { t } = useI18n();
const toast = useToast();
const instance = getCurrentInstance();
const $modal = instance?.appContext.config.globalProperties.$modal;
const $toast = instance?.appContext.config.globalProperties.$toast;

const dragOptions = computed(() => ({
  animation: 0,
  group: "tour-day",
  disabled: false,
  ghostClass: "ghost",
}));

const loadLocations = async () => {
  locations.value = await api.location.all();
};

const searchLocation = (locationId: number): string => {
  const location = locations.value.find((loc) => loc.id === locationId);
  return location ? location.name : "";
};

const getDate = (index: number) => {
  const initDate = DateTime.fromISO(props.initDate);
  const endDate = DateTime.fromISO(props.endDate);

  let date = initDate;
  const dates = [];

  while (date <= endDate) {
    dates.push(date.toFormat("yyyy-MM-dd"));
    date = date.plus({ days: 1 });
  }

  return dates[index] || null;
};

const change = async (event: any) => {
  // After drag, update the order of all days and persist to backend
  if (props.modelValue && Array.isArray(props.modelValue)) {
    const updates = props.modelValue.map(async (day: any, idx: number) => {
      if (day.order !== idx + 1) {
        day.order = idx + 1;
        await api.activityService.updateTourDay(props.agency, props.templateId, day.id, {
          is_stage: day.isStage,
          order: day.order,
          origin: day.originId,
          destination: day.destinationId,
        });
      }
    });
    await Promise.all(updates);
    emit("reloadTour");
  }
};

const addDay = (order: number) => {
  emit("emitAddDay", order);
};

const removeAllItems = async (tourDay: Record<string, any>) => {
  tourDay.items.forEach((item: Record<string, any>) => {
    api.activityService.deleteActivityDayItem(props.agency, props.templateId, tourDay.id, item.id);
  });
};

const removeDay = async (tourDay: Record<string, any>, dayNumber: number) => {
  await $modal.delete({
    title: t("template.deleteDayTitle"),
    text: t("template.deleteDayText", { dayNumber: dayNumber + 1 }),
    deleteButtonText: t("general.button.delete"),
    rightButtonClasses: "btn-tertiary",
    deleteButtonCallback: () => doRemoveDay(tourDay),
  });
};

const doRemoveDay = async (tourDay: Record<string, any>) => {
  const loadingToast = $toast.loading(t("general.button.deleting"), "toast-remove-tour-day");

  try {
    await removeAllItems(tourDay);

    await api.activityService.deleteTourDay(props.agency, props.templateId, tourDay.id);

    emit("reloadTour");

    loadingToast.update("toast-remove-tour-day", {
      content: t("template.dayDeleted"),
      options: {
        type: TYPE.SUCCESS,
        icon: "icon icon-check-circle-fill",
        timeout: 5000,
      },
    });
  } catch (e: any) {
    loadingToast.update("toast-remove-tour-day", {
      content: t("template.dayDeleteError"),
      options: {
        type: TYPE.ERROR,
        icon: "icon icon-exclamation-triangle-fill",
        timeout: 5000,
      },
    });
    console.error(e.response.data);
    toast.error(e.response.data.message);
  }
};

const updateTourDayLength = async (tourDay: Record<string, any>) => {
  if ((!tourDay.stageLength && tourDay.stageLength === 0) || (!!tourDay.stageLength && tourDay.stageLength >= 0)) {
    await updateTourDay(tourDay);
  }
};

const createLocation = async (
  tourDay: Record<string, any>,
  address: Record<string, any>,
  isOrigin = true,
  position = null
) => {
  const location = await api.location.create({
    name: address.name ? address.name : "-",
    provider_id: address.placeId,
    lat: address.lat,
    lng: address.lng,
    country: address.country.longName,
    province: address.regionProvince.longName,
    state: address.stateCommunity.longName,
  });

  if (isOrigin) {
    tourDay.originId = location.id;
  } else {
    tourDay.destinationId = location.id;
  }

  let positionCode = undefined;
  if (position !== null && props.tourMapped && position + 1 === props.tourMapped.tourDays.length) {
    positionCode = 1;
  } else if (position !== null && position === 0) {
    positionCode = position;
  }

  await updateTourDay(tourDay, positionCode);
};

const updateTourDayType = async (checkboxValue: boolean, tourDay: Record<string, any>) => {
  if (!isTourDayUpdating.value) {
    tourDay.type = checkboxValue ? "stage" : "rest";
    tourDay.isStage = checkboxValue;
    await updateTourDay(tourDay);
  }
};

const updateTourDayOrigin = async (value: string, tourDay: Record<string, any>, position = 0) => {
  if (!value) {
    tourDay.originId = null;
    await updateTourDay(tourDay, position);
  }
};

const updateTourDayDestination = async (value: string, tourDay: Record<string, any>) => {
  if (!value) {
    tourDay.destinationId = null;
    await updateTourDay(tourDay);
  }
};

const updateTourDay = async (tourDay: Record<string, any>, position: any = null) => {
  let positionCode = 0;
  if (position !== null && position === 0) {
    positionCode = 1; // Origin
  } else if (position !== null && position === 1) {
    positionCode = 2; // Destination
  }

  if (!isTourDayUpdating.value) {
    try {
      isTourDayUpdating.value = true;

      await api.activityService.updateTourDay(props.agency, props.templateId, tourDay.id, {
        is_stage: tourDay.isStage,
        order: tourDay.order,
        origin: tourDay.originId,
        destination: tourDay.destinationId,
      });

      isTourDayUpdating.value = false;
    } catch (e: any) {
      isTourDayUpdating.value = false;
      console.error(e);
      toast.error(e.response?.data?.message || "An error occurred");
    }
  }
};

onMounted(async () => {
  await loadLocations();
});
</script>

<style lang="scss">
.flip-list-move {
  transition: transform 0.5s;
}

.no-move {
  transition: transform 0s;
}

.list-group {
  min-height: 20px;
}

.list-enter-from {
  opacity: 0;
  transform: scaleY(0.25);
}

.list-enter-active,
.list-leave-active {
  transition: all 1s cubic-bezier(0.55, 0, 0.1, 1);
}

.list-leave-to {
  opacity: 0;
  transform: scaleY(0);
}

.list-move {
  transition: all 1s cubic-bezier(0.55, 0, 0.1, 1);
}

.list-leave-active {
  position: relative;
}

.list-group-item {
  display: block !important;
  padding: 0 !important;
  border: none !important;
  border-radius: 0 !important;
  width: 100% !important;
}
</style>
